import { ChangeDetectionStrategy, Component, computed, effect, inject, NgZone, OnDestroy, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { BerlinDate } from "@ankaadia/ankaadia-shared";
import { KpiResultFragment, KpiType } from "@ankaadia/graphql";
import { TranslocoService } from "@jsverse/transloco";
import { clone } from "lodash";
import { DropdownModule } from "primeng/dropdown";
import { TagModule } from "primeng/tag";
import { catchError, of, startWith, Subject, switchMap } from "rxjs";
import { MessageService } from "../../features/message/message.service";
import { AppDateTimePipe } from "../../shared/pipes/date.pipe";
import { TranslateDirective } from "../../shared/transloco/translate.directive";
import { NewUiKpiCarouselViewComponent } from "./kpi-carousel-view/new-ui-kpi-carousel-view.component";
import { NewUiKpiGridViewComponent } from "./kpi-grid-view/new-ui-kpi-grid-view.component";
import { KpisService } from "./kpis.service";

@Component({
  selector: "new-ui-kpi",
  templateUrl: "./new-ui-kpi.component.html",
  styleUrl: "./new-ui-kpi.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateDirective,
    RouterLink,
    NewUiKpiCarouselViewComponent,
    NewUiKpiGridViewComponent,
    AppDateTimePipe,
    FormsModule,
    DropdownModule,
    TagModule,
  ],
  host: { style: "display: block; height: 100%; overflow-y: auto" },
})
export class NewUiKpiComponent implements OnDestroy {
  readonly kpisService: KpisService = inject(KpisService);
  readonly messageService = inject(MessageService);
  private readonly transloco = inject(TranslocoService);
  private readonly ngZone = inject(NgZone);
  readonly language = this.transloco.getActiveLang();

  // Expose KpiType enum to template
  readonly KpiType = KpiType;
  readonly window = window;

  // Error state
  readonly error = signal<string | null>(null);
  readonly isLoading = signal<boolean>(true);
  readonly isRefreshing = signal<boolean>(false);

  // Layout and playback state
  readonly isCarouselMode = signal<boolean>(true);
  readonly isPlaying = signal<boolean>(true);
  readonly currentPanelIndex = signal<number>(0);
  readonly autoRefreshEnabled = signal<boolean>(true);

  // Month selection
  readonly selectedMonth = signal<{ year: number; month: number } | null>(null);
  private lastLoadedMonth: { year: number; month: number } | null = null;
  readonly currentMonth: { year: number; month: number } = {
    year: BerlinDate.today().getFullYear(),
    month: BerlinDate.today().getMonth() + 1,
  };

  // Fetch available months
  private readonly availableMonthsTrigger$ = new Subject<void>();
  readonly availableMonths$ = this.availableMonthsTrigger$.pipe(
    startWith(undefined),
    switchMap(() => this.kpisService.getAvailableMonths())
  );

  readonly availableMonthsSignal = toSignal(this.availableMonths$, {
    initialValue: [],
  });

  readonly availableMonthsOptions = computed(() => {
    const months = this.availableMonthsSignal();
    return months
      .map((m) => {
        return {
          label: `${m.month.toString().padStart(2, "0")}/${m.year}`,
          value: { year: m.year, month: m.month },
        };
      })
      .sort((a, b) => {
        if (a.value.year !== b.value.year) {
          return b.value.year - a.value.year;
        }
        return b.value.month - a.value.month;
      });
  });

  private autoPlayInterval: ReturnType<typeof setInterval> | null = null;
  private readonly AUTO_PLAY_INTERVAL = 15 * 1000; // 15 seconds

  private autoRefreshInterval: ReturnType<typeof setInterval> | null = null;
  private readonly AUTO_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

  // Refresh trigger
  private readonly refreshTrigger$ = new Subject<void>();

  // Writable signal for KPI data to allow clearing
  private readonly kpisWritableSignal = signal<KpiResultFragment | null>(null);

  readonly kpis$ = this.refreshTrigger$.pipe(
    startWith(undefined),
    switchMap(() =>
      this.kpisService
        .getKpis({
          types: Object.values(KpiType),
          month: this.selectedMonth() ?? this.currentMonth,
        })
        .pipe(
          catchError((err) => {
            const errorMessage = err?.message || this.transloco.translate("kpi.error.message");
            this.error.set(errorMessage);
            this.isLoading.set(false);
            this.isRefreshing.set(false);

            // Show error message to user
            this.messageService.add({
              severity: "error",
              summary: this.transloco.translate("kpi.error.title"),
              detail: errorMessage,
            });

            // Return empty result to prevent breaking the UI
            return of(null);
          })
        )
    )
  );

  // Convert kpis$ observable to a signal for reactive state management
  readonly kpisSignal = toSignal(this.kpis$, { initialValue: null });

  constructor() {
    // Trigger fetching available months
    this.availableMonthsTrigger$.next();

    // Use effect to manage loading state based on data availability
    effect(() => {
      const data = this.kpisSignal();
      if (data !== null || this.error()) {
        this.isLoading.set(false);
        this.isRefreshing.set(false);
        // Set selected month to current data month if not already set
        if (data && !this.selectedMonth()) {
          const date = data.date.toDate();
          this.selectedMonth.set({ year: date.getFullYear(), month: date.getMonth() + 1 });
        }
      }
    });

    // Auto-play effect
    effect(() => {
      if (this.isPlaying() && this.isCarouselMode()) {
        this.startAutoPlay();
      } else {
        this.stopAutoPlay();
      }
    });

    // Auto-refresh effect
    effect(() => {
      if (this.autoRefreshEnabled()) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    });

    // Reload data when selectedMonth changes
    effect(() => {
      const month = this.selectedMonth();
      if (month) {
        const monthChanged =
          !this.lastLoadedMonth ||
          this.lastLoadedMonth?.year !== month.year ||
          this.lastLoadedMonth?.month !== month.month;

        if (monthChanged) {
          this.refreshKpis(true); // Explicit refresh with data clear
        }
      }
    });
  }

  onMonthChange(value: { year: number; month: number }): void {
    this.selectedMonth.set(value);
  }

  refreshKpis(explicit = false): void {
    if (explicit) {
      // Explicit refresh: clear data and show loading state
      this.isLoading.set(true);
      this.kpisWritableSignal.set(null);
      this.error.set(null);
      // Update last loaded month
      const month = this.selectedMonth();
      if (month) {
        this.lastLoadedMonth = clone(month);
      }
    } else {
      // Silent refresh: just set refreshing state
      this.isRefreshing.set(true);
      this.error.set(null);
    }
    this.refreshTrigger$.next();
  }

  toggleAutoRefresh(): void {
    this.autoRefreshEnabled.update((value) => !value);
    // Trigger immediate refresh when enabling
    if (this.autoRefreshEnabled()) {
      this.refreshKpis(false); // Silent refresh
    }
  }

  ngOnDestroy(): void {
    this.stopAutoPlay();
    this.stopAutoRefresh();
  }

  // Computed signals for filtered KPIs
  readonly candidateKpis = computed(() => {
    const kpiData = this.kpisSignal();
    if (!kpiData) {
      return [];
    }
    const results = kpiData.results ?? [];
    return this.getCandidateKpis(results);
  });

  readonly organizationKpis = computed(() => {
    const results = this.kpisSignal()?.results ?? [];
    return this.getOrganizationKpis(results);
  });

  readonly revenueKpis = computed(() => {
    const results = this.kpisSignal()?.results ?? [];
    return this.getRevenueKpis(results);
  });

  readonly accountsReceivableKpis = computed(() => {
    const results = this.kpisSignal()?.results ?? [];
    return this.getAccountsReceivableKpis(results);
  });

  getCandidateKpis(results: KpiResultFragment[]): KpiResultFragment[] {
    // Show all candidate KPIs in a single row
    const types = [
      KpiType.CandidateTotal,
      KpiType.CandidateActive,
      KpiType.CandidateInactive,
      KpiType.CandidateMatched,
      KpiType.CandidateOnContingent,
    ];
    return types
      .map((type) => results.find((kpi) => kpi.type === type))
      .filter((kpi): kpi is KpiResultFragment => kpi !== undefined);
  }

  getOrganizationKpis(results: KpiResultFragment[]): KpiResultFragment[] {
    // Show all organization and user KPIs in a single row (max 5)
    const types = [
      KpiType.OrganizationTotal,
      KpiType.OrganizationLicensed,
      KpiType.OrganizationWithActiveUser,
      KpiType.UserTotal,
      KpiType.UserActive,
    ];
    return types
      .map((type) => results.find((kpi) => kpi.type === type))
      .filter((kpi): kpi is KpiResultFragment => kpi !== undefined);
  }

  getRevenueKpis(results: KpiResultFragment[]): KpiResultFragment[] {
    // Show all revenue KPIs in a single row
    const types = [
      KpiType.RevenueTotal,
      KpiType.RevenueCandidate,
      KpiType.RevenueLicense,
      KpiType.RevenueAddOnService,
      KpiType.RevenueCreditNote,
    ];
    return types
      .map((type) => results.find((kpi) => kpi.type === type))
      .filter((kpi): kpi is KpiResultFragment => kpi !== undefined);
  }

  getAccountsReceivableKpis(results: KpiResultFragment[]): KpiResultFragment[] {
    // Show all revenue KPIs in a single row
    const types = [
      KpiType.AccountsReceivableNet,
      KpiType.AccountsReceivableCandidate,
      KpiType.AccountsReceivableLicense,
      KpiType.AccountsReceivableAddOnService,
      KpiType.AccountsReceivableCreditNote,
    ];
    return types
      .map((type) => results.find((kpi) => kpi.type === type))
      .filter((kpi): kpi is KpiResultFragment => kpi !== undefined);
  }

  toggleLayoutMode(): void {
    this.isCarouselMode.update((value) => !value);
    if (!this.isCarouselMode()) {
      this.stopAutoPlay();
      this.isPlaying.set(false);
    }
  }

  togglePlay(): void {
    this.isPlaying.update((value) => !value);
  }

  goToPanel(index: number): void {
    this.currentPanelIndex.set(index);
    // Reset auto-play timer when manually navigating
    if (this.isPlaying()) {
      this.stopAutoPlay();
      this.startAutoPlay();
    }
  }

  getPrevIndex(): number {
    const current = this.currentPanelIndex();
    const totalPanels = 3; // candidates, organizations, revenue
    return current === 0 ? totalPanels - 1 : current - 1;
  }

  getNextIndex(): number {
    const current = this.currentPanelIndex();
    const totalPanels = 3; // candidates, organizations, revenue
    return current === totalPanels - 1 ? 0 : current + 1;
  }

  private startAutoPlay(): void {
    this.stopAutoPlay();
    // Run outside Angular zone to prevent triggering change detection on every tick
    this.ngZone.runOutsideAngular(() => {
      this.autoPlayInterval = setInterval(() => {
        this.ngZone.run(() => {
          this.currentPanelIndex.update((current) => {
            const totalPanels = 3; // candidates, organizations, revenue
            return current === totalPanels - 1 ? 0 : current + 1;
          });
        });
      }, this.AUTO_PLAY_INTERVAL);
    });
  }

  private stopAutoPlay(): void {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    // Run outside Angular zone to prevent triggering change detection on every tick
    this.ngZone.runOutsideAngular(() => {
      this.autoRefreshInterval = setInterval(() => {
        this.ngZone.run(() => {
          this.refreshKpis();
        });
      }, this.AUTO_REFRESH_INTERVAL);
    });
  }

  private stopAutoRefresh(): void {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
      this.autoRefreshInterval = null;
    }
  }
}
